import { QueryClient } from '@tanstack/react-query';

// Previous configuration - very aggressive data freshness but poor performance
// const queryClient = new QueryClient({
//   defaultOptions: {
//     queries: {
//       staleTime: 0, // Consider data stale immediately
//       cacheTime: 0, // Disable caching completely
//       refetchOnMount: 'always', // Always refetch on mount
//       refetchOnWindowFocus: false, // Don't refetch on window focus
//       retry: false,
//     },
//     mutations: {
//       retry: false,
//     },
//   },
// });

// Optimized configuration for better performance while maintaining data freshness
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes - good balance between freshness and performance
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Keep inactive queries in cache for 10 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes

      // Only refetch if data is stale (respects staleTime)
      refetchOnMount: true,

      // Prevent refetching when user returns to tab
      refetchOnWindowFocus: false,

      // Keep previous data while fetching new data (better UX)
      keepPreviousData: true,

      // Retry failed requests with exponential backoff (max 3 attempts)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors (network issues, 5xx errors)
        // return failureCount < 3;
        return false;
      },

      // Exponential backoff delay for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch interval for critical real-time data (disabled by default)
      // refetchInterval: false,

      // Background refetch when data becomes stale
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once for network errors only.
      // NOTE: errors from the GraphQL apiClient interceptor carry `statusCode`
      // (a plain Error), NOT `response.status`. The old guard read
      // `error.response.status`, which was always undefined, so the 4xx check
      // never tripped and EVERY failed mutation was silently retried once.
      retry: (failureCount, error) => {
        const status = error?.statusCode ?? error?.response?.status;
        // Don't retry client errors (4xx)
        if (status >= 400 && status < 500) {
          return false;
        }
        // Retry once for network/server errors
        return failureCount < 1;
      },

      // Retry delay for mutations
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
  },
});

export default queryClient;
